/**
 * Catchball Virtual Participant Configuration
 *
 * A cycle-level override that replaces org-chart-derived participant
 * resolution with an explicit user/group selection.
 *
 * Two modes:
 *   'org_chart' — default behaviour: tiers are derived from cycle.orgScopeNodeId
 *                 and the org unit hierarchy (existing logic).
 *   'virtual'   — participants are taken from this config's ball_recipients and
 *                 default_participants arrays.
 *
 * Tier mapping in virtual mode:
 *   executive — the cycle owner (always implicit; not stored here)
 *   middle    — ball_recipients (receive the toss)
 *   bottom    — default_participants (contribute below the recipients)
 *
 * Ad-hoc additions: the current ball holder may add further participants during
 * a loop via the existing loop levelConfig mechanism. Those additions are local
 * to the loop and do not modify this config.
 */

// ── Participant Reference ─────────────────────────────────────────────────

export type CatchballParticipantType = 'user' | 'group';

export interface CatchballParticipantRef {
  type: CatchballParticipantType;
  id: string;
  displayName: string;
}

// ── Setup Mode ─────────────────────────────────────────────────────────────

export type CatchballSetupMode = 'org_chart' | 'virtual';

// ── Virtual Config ─────────────────────────────────────────────────────────

export interface CatchballVirtualConfig {
  id: string;
  cycleId: string;
  workspaceId: string;
  setupMode: CatchballSetupMode;
  /** Users/groups that receive the ball when tossed (middle tier). */
  ballRecipients: CatchballParticipantRef[];
  /** Users/groups that can participate by default (bottom tier). */
  defaultParticipants: CatchballParticipantRef[];
  createdBy?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

// ── DB ↔ TS converters ─────────────────────────────────────────────────────

export interface CatchballVirtualConfigRow {
  id: string;
  cycle_id: string;
  workspace_id: string;
  setup_mode: string;
  ball_recipients: CatchballParticipantRef[];
  default_participants: CatchballParticipantRef[];
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export function rowToVirtualConfig(row: CatchballVirtualConfigRow): CatchballVirtualConfig {
  return {
    id: row.id,
    cycleId: row.cycle_id,
    workspaceId: row.workspace_id,
    setupMode: row.setup_mode as CatchballSetupMode,
    ballRecipients: Array.isArray(row.ball_recipients) ? row.ball_recipients : [],
    defaultParticipants: Array.isArray(row.default_participants) ? row.default_participants : [],
    createdBy: row.created_by,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
  };
}

export function virtualConfigToRow(
  config: Omit<CatchballVirtualConfig, 'id' | 'createdAt' | 'updatedAt'>
): Omit<CatchballVirtualConfigRow, 'id' | 'created_at' | 'updated_at'> {
  return {
    cycle_id: config.cycleId,
    workspace_id: config.workspaceId,
    setup_mode: config.setupMode,
    ball_recipients: config.ballRecipients,
    default_participants: config.defaultParticipants,
    created_by: config.createdBy ?? null,
  };
}

// ── Utility helpers ─────────────────────────────────────────────────────────

/**
 * Returns the user IDs explicitly named in the virtual config participant list.
 * Does NOT resolve group memberships (that requires an async DB call).
 */
export function getDirectUserIds(refs: CatchballParticipantRef[]): string[] {
  return refs.filter((r) => r.type === 'user').map((r) => r.id);
}

/**
 * Returns the group IDs from a participant list.
 */
export function getGroupIds(refs: CatchballParticipantRef[]): string[] {
  return refs.filter((r) => r.type === 'group').map((r) => r.id);
}

/**
 * Returns true if a userId is directly listed in the refs (user-type entries only).
 * Caller is responsible for resolving group memberships separately if needed.
 */
export function isUserDirectlyListed(userId: string, refs: CatchballParticipantRef[]): boolean {
  return refs.some((r) => r.type === 'user' && r.id === userId);
}
